/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Button, PageNotification, Typography2 } from '@coursera/cds-core';
import { ComposeIcon } from '@coursera/cds-icons';

import { CMLOrHTML } from 'bundles/cml';
import InlineSubmissionTool from 'bundles/compound-assessments/components/form-parts/boxViewDocumentAnnotation/InlineSubmissionTool';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { typeNames } from 'bundles/compound-assessments/constants';
import type {
  BoxViewDocumentAnnotationPrompt,
  BoxViewDocumentAnnotationResponse,
} from 'bundles/compound-assessments/types/FormParts';

import _t from 'i18n!nls/compound-assessments';

export type Props = {
  prompt: BoxViewDocumentAnnotationPrompt;
  responseId: string;
  courseId: string;
  itemId: string;
  userId: number;
  isReadOnly: boolean;
  isGraderView: boolean;
  onChangeResponse: (response: BoxViewDocumentAnnotationResponse) => void;
  isExpanded?: boolean;
  isQuickGraderContext?: boolean;
};

type State = {
  showInlineSubmissionTool: boolean;
};

export const checkInvalid = (): FormPartsValidationStatus | null => {
  return null;
};

const styles = {
  notification: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
  `,
  prompt: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
  `,
  toolEmbed: () => css`
    width: 100%;
    height: 500px;
  `,
};

class BoxViewDocumentAnnotation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { showInlineSubmissionTool: props.isExpanded || false };
  }

  componentDidMount() {
    this.markAnnotationAsCompletedIfNecessary();
  }

  // toggle the tool and trigger completion status when quick grader triggers isExpanded state
  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.isQuickGraderContext && prevProps.isExpanded !== this.props.isExpanded) {
      this.toggleInlineSubmissionTool();
    }
  }

  markAnnotationAsCompletedIfNecessary = () => {
    const { onChangeResponse, isReadOnly } = this.props;
    const { showInlineSubmissionTool } = this.state;

    if (showInlineSubmissionTool && !isReadOnly) {
      onChangeResponse({
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          reviewPart: {
            typeName: 'boxViewDocumentAnnotation',
            definition: {
              annotationComplete: true,
              fileId: '',
            },
          },
        },
      });
    }
  };

  toggleInlineSubmissionTool = () => {
    this.setState(
      ({ showInlineSubmissionTool }) => {
        return {
          showInlineSubmissionTool: !showInlineSubmissionTool,
        };
      },
      () => this.markAnnotationAsCompletedIfNecessary()
    );
  };

  render() {
    const { prompt, responseId, courseId, itemId, userId, isReadOnly, isGraderView, isQuickGraderContext } = this.props;
    const { showInlineSubmissionTool } = this.state;

    if (!prompt) {
      return null;
    }

    const editText = isReadOnly ? _t('View inline feedback') : _t('Give inline feedback');

    return (
      <div className="rc-BoxViewDocumentAnnotation">
        <Typography2
          className="rc-BoxViewDocumentAnnotation__prompt"
          component="div"
          variant="bodyPrimary"
          css={styles.prompt}
        >
          <CMLOrHTML value={prompt.promptContent} />
        </Typography2>
        {!isQuickGraderContext && isGraderView && isReadOnly && (
          <PageNotification severity="information" css={styles.notification}>
            {_t('Grade visibility must be hidden to give inline feedback.')}
          </PageNotification>
        )}
        {showInlineSubmissionTool && (
          <div css={styles.toolEmbed}>
            <InlineSubmissionTool
              reviewResponseId={responseId}
              courseId={courseId}
              itemId={itemId}
              userId={userId}
              annotationsDisabled={isReadOnly}
              isGraderView={isGraderView}
            />
          </div>
        )}
        {!showInlineSubmissionTool && !isQuickGraderContext && (
          <div>
            <Button
              size="medium"
              variant="ghost"
              iconPosition="before"
              icon={<ComposeIcon />}
              onClick={this.toggleInlineSubmissionTool}
            >
              {editText}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default BoxViewDocumentAnnotation;
